import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, AlertTriangle, Printer, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const ParReport = () => {
  const { isAdmin } = useAuth();
  const [branches, setBranches] = useState([]);
  const [parData, setParData] = useState(null);
  const [parBranch, setParBranch] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchPARReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (parBranch !== 'all') params.branch_id = parBranch;
      const resp = await api.get('/reports/par', { params });
      setParData(resp.data);
    } catch { toast.error('Failed to fetch PAR report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => parBranch === 'all' ? 'All Branches' : branches.find(b => b.id === parBranch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!parData) { toast.error('Generate the report first'); return; }
    const buildRows = (loans, bucket) => (loans || []).map((l, i) => ({
      'Bucket': bucket,
      'SN': i + 1,
      'Loan ID': l.loan_id,
      'Entrepreneur': l.entrepreneur_name,
      'Branch': l.branch_name || '',
      'Loan Amount': Number(l.loan_amount || 0),
      'Days Overdue': l.days_overdue || 0,
      'Principal O/S': Number(l.principal_outstanding || 0),
      'Interest O/S': Number(l.interest_outstanding || 0),
    }));
    const rows = [
      ...buildRows(parData.par_30?.loans, 'PAR 30+'),
      ...buildRows(parData.par_90?.loans, 'PAR 90+'),
      ...buildRows(parData.par_180?.loans, 'PAR 180+'),
    ];
    const s = parData.summary || {};
    exportToExcel({
      filename: `WWAGS_PAR_${getBranchName().replace(/\s+/g,'_')}`,
      sheetName: 'PAR Report',
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Active Loans', value: parData.total_active_loans || 0 },
        { label: 'Portfolio Outstanding', value: Math.round(parData.total_portfolio_outstanding || 0) },
        { label: 'PAR 30+ Loans', value: parData.par_30?.count || 0 },
        { label: 'PAR 90+ Loans', value: parData.par_90?.count || 0 },
        { label: 'PAR 180+ Loans', value: parData.par_180?.count || 0 },
        { label: 'Overall PAR %', value: `${(s.overall_par_percentage || 0).toFixed(2)}%` },
      ],
    });
  };

  const handlePrint = () => {
    if (!parData) return;
    const s = parData.summary || {};
    const cats = [{ label: 'PAR 30+ Days', data: parData.par_30 }, { label: 'PAR 90+ Days', data: parData.par_90 }, { label: 'PAR 180+ Days', data: parData.par_180 }];
    const catHTML = cats.map(c => {
      const rows = (c.data?.loans || []).map((l, i) => `<tr style="background:${i%2===0?'#fff':'#f9f9f9'}"><td>${l.loan_id}</td><td>${l.entrepreneur_name}</td><td>${l.branch_name||'-'}</td><td style="text-align:right">Rs.${(l.loan_amount||0).toLocaleString('en-IN')}</td><td style="text-align:center">${l.days_overdue||0}</td><td style="text-align:right">Rs.${(l.principal_outstanding||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(l.interest_outstanding||0).toLocaleString('en-IN')}</td></tr>`).join('');
      return `<h3>${c.label}</h3><p>(${c.data?.count||0} loans) Outstanding: Rs.${(c.data?.outstanding_principal||0).toLocaleString('en-IN')} | ${(c.data?.percentage||0).toFixed(2)}%</p>${(c.data?.loans||[]).length > 0 ? `<table><thead><tr><th>Loan ID</th><th>Entrepreneur</th><th>Branch</th><th>Loan Amt</th><th>Days O/D</th><th>Principal O/S</th><th>Interest O/S</th></tr></thead><tbody>${rows}</tbody></table>` : '<p>No loans in this category</p>'}`;
    }).join('');
    const body = `<div class="cards"><div class="card"><small>Total Active Loans</small><b>${parData.total_active_loans||0}</b></div><div class="card"><small>Portfolio Outstanding</small><b>Rs.${(parData.total_portfolio_outstanding||0).toLocaleString('en-IN')}</b></div><div class="card"><small>Total At Risk</small><b>${s.total_at_risk_loans||0} loans</b></div><div class="card"><small>Overall PAR %</small><b>${(s.overall_par_percentage||0).toFixed(1)}%</b></div></div>${catHTML}`;
    openPrintWindow(buildPrintHTML('Portfolio at Risk (PAR) Report', body, getBranchName()));
  };

  const renderCategory = (label, data, color) => (
    <div className="mb-6">
      <h3 className={`text-base font-semibold mb-2 text-${color}-900`}>{label} <span className="text-sm font-normal text-slate-500">({data?.count||0} loans | {(data?.percentage||0).toFixed(2)}%)</span></h3>
      {(data?.loans||[]).length > 0 ? (
        <div className="overflow-x-auto bg-white rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className={`bg-${color}-700 text-white`}><tr><th className="px-3 py-2 text-left">Loan ID</th><th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">Branch</th><th className="px-3 py-2 text-right">Loan Amount</th><th className="px-3 py-2 text-center">Days Overdue</th><th className="px-3 py-2 text-right">Principal O/S</th><th className="px-3 py-2 text-right">Interest O/S</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.loans.map((l, i) => (
                <tr key={l.loan_id} className={i % 2 === 0 ? '' : 'bg-slate-50'}>
                  <td className="px-3 py-2 font-medium"><Link to={`/loans/${l.loan_id}`} className="text-blue-700 hover:underline">{l.loan_id}</Link></td>
                  <td className="px-3 py-2">{l.entrepreneur_name}</td>
                  <td className="px-3 py-2 text-slate-600">{l.branch_name}</td>
                  <td className="px-3 py-2 text-right">Rs.{(l.loan_amount||0).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-center font-bold text-red-600">{l.days_overdue}</td>
                  <td className="px-3 py-2 text-right text-orange-600">Rs.{(l.principal_outstanding||0).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-right text-purple-600">Rs.{(l.interest_outstanding||0).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-slate-500 ml-2">No loans in this category</p>}
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="par-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">PAR Report</h1><p className="text-sm text-slate-600">Portfolio at Risk - 30/90/180+ days overdue</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        {isAdmin() && <Select value={parBranch} onValueChange={setParBranch}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent></Select>}
        <Button onClick={fetchPARReport} className="bg-red-700 hover:bg-red-800 text-white" data-testid="fetch-par-report-btn">Generate</Button>
        {parData && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {parData && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div></div>}

      {parData && !loading && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center"><p className="text-xs text-slate-600 mb-1">Active Loans</p><p className="text-xl font-bold">{parData.total_active_loans}</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center"><p className="text-xs text-blue-600 mb-1">Portfolio O/S</p><p className="text-lg font-bold text-blue-800">Rs.{(parData.total_portfolio_outstanding||0).toLocaleString('en-IN')}</p></div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center"><p className="text-xs text-red-600 mb-1">At Risk Loans</p><p className="text-xl font-bold text-red-800">{parData.summary?.total_at_risk_loans||0}</p></div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center"><p className="text-xs text-amber-600 mb-1">Overall PAR %</p><p className="text-xl font-bold text-amber-800">{(parData.summary?.overall_par_percentage||0).toFixed(1)}%</p></div>
          </div>
          {renderCategory('PAR 30+ Days', parData.par_30, 'amber')}
          {renderCategory('PAR 90+ Days', parData.par_90, 'orange')}
          {renderCategory('PAR 180+ Days', parData.par_180, 'red')}
        </div>
      )}

      {!parData && !loading && <div className="text-center py-12 text-slate-500"><AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Click "Generate" to view PAR report</p></div>}
    </div>
  );
};

export default ParReport;
